<template lang="pug">
#inspection-reservation-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設見学の受付登録・修正

    .box-body.register-body
      //- applicationDatetime
      FormLabel(v-if="inspectionReservation" key-name="申込日時" :value="_applicationDatetime")
      //- approved
      .d-flex.flex-column.mb-3(style="margin-left:20px;")
        span.approved-name 受付状態
        ApprovedSwitch(v-model="approved")
      //- datetime
      FormDatetimePicker(v-model="datetime" :date.sync="datetimeDate" :time.sync="datetimeTime" name="datetime" key-name="見学日時" placeholder="見学日時"
        :required="_requiredDatetime")
      //- name
      FormLabel(v-if="inspectionReservation" key-name="氏名" :value="inspectionReservation.name")
      //- tel
      FormLabel(v-if="inspectionReservation" key-name="電話番号" :value="inspectionReservation.tel")
      //- applicantMessage
      FormLabel(v-if="inspectionReservation" key-name="申込者からの連絡事項" :value="inspectionReservation.applicantMessage")
      //- customerName
      FormInput(v-model="customerName" key-name="担当者" name="customerName" type="text" placeholder="担当者" :maxlength="100"
        :validation-rules="{ max: 100 }")
      //- receptionMemo
      FormTextarea(v-model="receptionMemo" key-name="受付メモ" name="reception-memo" placeholder="受付メモ" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="onUpdate" type="button") 更新
        button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  OkCancelModal(v-model="showUpdateConfirmModal" title="施設見学情報を更新しますか？" @ok="updateInspectionReservation")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoInspectionReservationList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import InspectionReservationsService, { InspectionReservationsGetModel, InspectionReservationsConsentedPatchModel } from "@/commons/services/InspectionReservations";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import ApprovedSwitch from "@/pc/basics/ApprovedSwitch.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * ## 施設見学編集画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    ApprovedSwitch,
    FormDatetimePicker,
    FormInput,
    FormTextarea,
    FormLabel
  },
  $_veeValidate: { validator: "new" }
})
export default class InspectionReservationEdit extends Vue {
  /** 施設見学サービス */
  @Inject("InspectionReservationsService") inspectionReservationsService!: InspectionReservationsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateConfirmModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設見学 */
  inspectionReservation: InspectionReservationsGetModel | null = null;
  /** 施設見学、受付状態 */
  approved: boolean = false;
  /** 見学日時 */
  datetime: number | null = null;
  /** 見学日時(日付) */
  datetimeDate: string = "";
  /** 見学日時(開始時刻) */
  datetimeTime: string = "";
  /** 担当者 */
  customerName: string | null = "";
  /** 施設見学、受付メモ */
  receptionMemo: string | null = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  get _applicationDatetime(): string {
    if (!this.inspectionReservation) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(this.inspectionReservation.applicationDatetime, "yyyy年mm月dd日 hh：MM");
  }

  get _requiredDatetime(): boolean {
    return this.approved;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 既存値を初期入力するために施設見学をAPIから取得する
   *   - 前画面から渡された施設見学のソートキーを取得して使用する
   * - 見学日時の値は既存入力されていれば復元し、無ければカレンダー上の初期値を作成する
   */
  public async createView(): Promise<void> {
    const inspectionReservationsSortKey: string = this.$route.params.datetime;
    const inspectionReservation: InspectionReservationsGetModel = await this.getInspectionReservation(inspectionReservationsSortKey);
    this.inspectionReservation = inspectionReservation;

    this.approved = inspectionReservation.approved;
    if (inspectionReservation.datetime) {
      const datetimeDate: string = this.$dateUtil.convertNumberToString(inspectionReservation.datetime, "yyyy-mm-dd");
      const datetimeTime: string = this.$dateUtil.convertNumberToString(inspectionReservation.datetime, "hh:MM:ss");
      this.datetime = inspectionReservation.datetime;
      this.datetimeDate = datetimeDate;
      this.datetimeTime = datetimeTime;
    } else {
      const datetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
      const datetimeTime: string = "10:00:00";
      this.datetimeDate = datetimeDate;
      this.datetimeTime = datetimeTime;
    }
    this.customerName = inspectionReservation.customerName ? inspectionReservation.customerName : "";
    this.receptionMemo = inspectionReservation.receptionMemo ? inspectionReservation.receptionMemo : "";

    return;
  }

  /**
   * ### 編集対象の施設見学取得
   */
  public async getInspectionReservation(inspectionReservationsSortKey: string): Promise<InspectionReservationsGetModel> {
    const response: InspectionReservationsGetModel = await this.inspectionReservationsService.get(userStore, inspectionReservationsSortKey);
    return response;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   * - 受付状態が「受付済」の場合は見学日時の必須チェックを行う
   */
  public async onUpdate(): Promise<void> {
    this.$validator.errors.remove("datetime");

    const validationResult: boolean = await this.$validator.validateAll();

    let datetimeValidationResult: boolean = true;
    if (this.approved == true && this.datetime == null) {
      this.$validator.errors.add({ field: "datetime", msg: "受付状態を受付済にする場合、見学日時は必須項目です" });
      datetimeValidationResult = false;
    }

    if (datetimeValidationResult && validationResult) {
      this.showValidationAlert = false;
      this.showUpdateConfirmModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 施設見学を更新する
   */
  public async updateInspectionReservation(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const inspectionReservation: InspectionReservationsGetModel = this.inspectionReservation as InspectionReservationsGetModel;
      const requestBody: InspectionReservationsConsentedPatchModel = this.createRequestBody(inspectionReservation);
      await this.inspectionReservationsService.patch(userStore, inspectionReservation.inspectionReservationsSortKey, requestBody);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    // サイドメニューを更新
    this.$emit("refresh");

    return;
  }

  /**
   * ### 施設見学更新のリクエストボディを作成する
   */
  public createRequestBody(inspectionReservation: InspectionReservationsGetModel): InspectionReservationsConsentedPatchModel {
    return {
      approved: this.approved,
      datetime: this.datetime,
      receptionMemo: this.receptionMemo ? this.receptionMemo : null,
      customerName: this.customerName ? this.customerName : null,
      updated: inspectionReservation.updated
    };
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.gotoInspectionReservationList();
    return;
  }

  /**
   * ### 施設見学一覧に遷移する
   */
  public gotoInspectionReservationList(): void {
    this.$router.push(PcUrl.InspectionReservationList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#inspection-reservation-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .approved-name {
    height: 27px;
    margin-bottom: 4px;
    font-size: 18px;
    font-weight: bold;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
